import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X, Phone, ExternalLink, Copy, Check, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SessionHistoryProps } from './cfg';
import { Typography } from 'interfaces/typography';
import { SessionDetailView } from 'components/History/views';
import { SessionListResponse } from 'interfaces/sessions';
import { Platform, PLATFORM_TO_HUMAN_READABLE, ConversationType } from 'interfaces/db';
import { SessionV2Columns } from 'interfaces/table';
import { minimalDateToString, shortenedDateToString } from 'time';
import { isSessionAnswered } from 'utils/session';
import { formatPhoneNumber } from 'utils/format';

export type SessionHistoryHeaderProps = {
  onClose: () => void;
  view: SessionDetailView;
  name: string;
  jobTitle?: string;
  count?: number;
  sessionId?: string;
  phone_numbers?: string[];
  platform?: Platform;
  platform_url?: string;
  company_name?: string;
  sessions: SessionListResponse[];
} 

export interface SessionFilters {
  dispositions: Set<string>;
  sentiments: Set<string>;
  purposes: Set<string>;
  objections: Set<string>;
  recipientTypes: Set<string>;
  messageTypes: Set<ConversationType>;
} 

export const SessionHistoryHeader: React.FC<SessionHistoryHeaderProps> = ({
  onClose,
  name,
  jobTitle,
  count,
  phone_numbers,
  platform,
  platform_url,
  company_name,
  sessions,
}) => {
  const [copiedPhoneNumber, setCopiedPhoneNumber] = useState<string | null>(null);
  const [isPhoneDropdownOpen, setIsPhoneDropdownOpen] = useState(false);
  const phoneDropdownRef = useRef<HTMLDivElement>(null);
 
  const lastCall = useMemo(() => {
    if (!sessions.length) return null;
    const answeredSessions = sessions.filter(session => session.result.get(SessionV2Columns.CONVERSATION_TYPE) === ConversationType.CALL);
    if (answeredSessions.length === 0) return null;
    const sortedSessions = [...answeredSessions].sort((a, b) => b.start.getTime() - a.start.getTime());
    return sortedSessions[0].start;
  }, [sessions]);

  // Get the last connection date
  const lastConnect = useMemo(() => {
    if (!sessions.length) return null;
    const answeredSessions = sessions.filter(session => isSessionAnswered(session));
    if (answeredSessions.length === 0) return null;
    const sortedSessions = [...answeredSessions].sort((a, b) => b.start.getTime() - a.start.getTime());
    return sortedSessions[0].start;
  }, [sessions]);

  const handleCopyPhoneNumber = async (phoneNumber: string) => {
    try {
      await navigator.clipboard.writeText(phoneNumber);
      setCopiedPhoneNumber(phoneNumber);
      setTimeout(() => setCopiedPhoneNumber(null), 2000);
    } catch (err) {
      console.error('Failed to copy phone number:', err);
    }
  };

  const renderPhoneNumbers = (phoneNumbers: string[]) => {
    const primaryPhone = formatPhoneNumber(phoneNumbers[0]);
    const additionalCount = phoneNumbers.length > 1 ? phoneNumbers.length - 1 : 0;

    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <Phone size={14} className="text-gray-400" />
          <Typography variant="mediumParagraph" color="#111827">
            {primaryPhone}
            {additionalCount > 0 && ` - ${additionalCount} more`}
          </Typography>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCopyPhoneNumber(phoneNumbers[0]);
          }}
          className="p-1 hover:bg-gray-100 rounded-md transition-colors"
          title="Copy phone number"
        >
          {copiedPhoneNumber === phoneNumbers[0] ? (
            <Check size={14} className="text-green-600" />
          ) : (
            <Copy size={14} className="text-gray-400" />
          )}
        </button>
      </div>
    );
  };


  return (
    <div className="w-full border-b border-gray-100 bg-white sticky top-0 z-10 shadow-sm">
      <div className="flex flex-col px-4 py-3">
        {/* Title and action buttons */}
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-x-2 max-w-[75%]">
            <Typography variant="h1" className="font-bold truncate text-xl leading-7">
              {name}
            </Typography>
            {(jobTitle || company_name) && (
              <Typography variant="mediumParagraph" color="gray" className="truncate text-sm">
                {[jobTitle, company_name].filter(Boolean).join(' • ')}
              </Typography>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {/* Platform link */}
            {platform && platform_url && (
              <a
                href={platform_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <Typography variant="mediumParagraph" color="#2563EB">
                  {PLATFORM_TO_HUMAN_READABLE[platform]}
                </Typography>
                <ExternalLink size={14} />
              </a>
            )}
            
            <button 
              onClick={onClose}
              className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Close conversation history"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Bottom row with contact info */}
        <div className="flex items-center gap-3 text-sm text-gray-600">
          {/* Phone numbers */}
          {phone_numbers && phone_numbers.length > 0 && renderPhoneNumbers(phone_numbers)}

          {/* Conversation count and status */}
          {count !== undefined && (
            <>
              <Typography variant="mediumParagraph" color="#D1D5DB">•</Typography>
              <div className="flex items-center gap-2">
                <Typography variant="mediumParagraph" color="#6B7280">
                  {count} {count === 1 ? 'call' : 'calls'}
                </Typography>
                {sessions.length > 0 && (
                  <>
                    <Typography variant="mediumParagraph" color="#D1D5DB">•</Typography>
                    {sessions.some(session => isSessionAnswered(session)) ? (
                      <Typography variant="mediumParagraph" color="#059669" fontWeight="500">
                        Status: Last Reached{lastConnect ? ` - ${minimalDateToString(lastConnect)}` : ''}
                      </Typography>
                    ) : (
                      <Typography variant="mediumParagraph" color="#DC2626" fontWeight="500">
                        Status: Never Reached{lastCall ? ` - ${minimalDateToString(lastCall)}` : ''}
                      </Typography>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}; 